import { ENV } from '../../config/runtimeEnv';
import {
	RefSeq2,
	ChromosomeDistribution
} from '../../types/geneCodes/RefSeq2Types';
import { http } from '../../lib/http/client';

const API_BASE_URL = ENV.VITE_LABIQ_SERVER || '';

// Helper for common API headers

/**
 * Fetch RefSeq2 records with pagination and filters
 */
export const fetchRefSeq2Records = async (
	page: number = 1,
	perPage: number = 30,
	sortBy: string = 'idrefseq2_ID',
	sortOrder: string = 'asc',
	filters: {
		gene?: string;
		chrom?: string;
		search?: string;
	} = {}
): Promise<{
	data: RefSeq2[];
	total: number;
}> => {
	// Remove empty string filters
	const cleanedFilters = Object.fromEntries(
		Object.entries(filters).filter(([_, value]) => value !== "")
	);

	const response = await http.get(`${API_BASE_URL}/refseq`, {
		params: {
			page,
			per_page: perPage,
			sort_by: sortBy,
			sort_order: sortOrder,
			...cleanedFilters
		}
	});

	return {
		data: response.data.data,
		total: response.data.total
	};
};

/**
 * Fetch a single RefSeq2 record by ID
 */
export const fetchRefSeq2Record = async (id: number): Promise<RefSeq2> => {
	const response = await http.get(`${API_BASE_URL}/refseq/${id}`);

	return response.data.data;
};

/**
 * Create a new RefSeq2 record
 */
export const createRefSeq2Record = async (refseq: Partial<RefSeq2>): Promise<RefSeq2> => {
	const response = await http.post(
		`${API_BASE_URL}/refseq`,
		refseq
	);

	return response.data.data;
};

/**
 * Update an existing RefSeq2 record
 */
export const updateRefSeq2Record = async (id: number, refseq: Partial<RefSeq2>): Promise<RefSeq2> => {
	const response = await http.put(
		`${API_BASE_URL}/refseq/${id}`,
		refseq
	);

	return response.data.data;
};

/**
 * Delete a RefSeq2 record
 */
export const deleteRefSeq2Record = async (id: number): Promise<void> => {
	await http.delete(`${API_BASE_URL}/refseq/${id}`);
};

/**
 * Get RefSeq2 records by chromosome
 */
export const getRefSeq2ByChromosome = async (
	chromosome: string,
	page: number = 1,
	perPage: number = 30,
	sortBy: string = 'idrefseq2_ID',
	sortOrder: string = 'asc',
	gene?: string
): Promise<{
	data: RefSeq2[];
	total: number;
	chromosome: string;
}> => {
	const response = await http.get(`${API_BASE_URL}/refseq/chromosome/${chromosome}`, {
		params: {
			page,
			per_page: perPage,
			sort_by: sortBy,
			sort_order: sortOrder,
			gene: gene === "" ? undefined : gene
		}
	});

	return {
		data: response.data.data,
		total: response.data.total,
		chromosome: response.data.chromosome
	};
};

/**
 * Get RefSeq2 records by gene name
 */
export const getRefSeq2ByGene = async (
	gene: string,
	page: number = 1,
	perPage: number = 30,
	sortBy: string = 'idrefseq2_ID',
	sortOrder: string = 'asc'
): Promise<{
	data: RefSeq2[];
	total: number;
	gene: string;
}> => {
	const response = await http.get(`${API_BASE_URL}/refseq/gene/${gene}`, {
		params: {
			page,
			per_page: perPage,
			sort_by: sortBy,
			sort_order: sortOrder
		}
	});

	return {
		data: response.data.data,
		total: response.data.total,
		gene: response.data.gene
	};
};

/**
 * Search RefSeq2 records by definition
 */
export const searchRefSeq2Definition = async (
	search: string,
	page: number = 1,
	perPage: number = 30,
	sortBy: string = 'idrefseq2_ID',
	sortOrder: string = 'asc'
): Promise<{
	data: RefSeq2[];
	total: number;
	search: string;
}> => {
	const response = await http.post(
		`${API_BASE_URL}/refseq/search/definition`,
		{ search },
		{
			params: {
				page,
				per_page: perPage,
				sort_by: sortBy,
				sort_order: sortOrder
			}
		}
	);

	return {
		data: response.data.data,
		total: response.data.total,
		search: response.data.search
	};
};

/**
 * Get chromosome distribution from RefSeq2
 */
export const getRefSeq2ChromosomeDistribution = async (): Promise<ChromosomeDistribution[]> => {
	const response = await http.get(`${API_BASE_URL}/refseq/distribution/chromosome`);

	return response.data.data;
};